// TopPillNav — horizontal pill-shaped navigation for the admin shell.
//
// Phase 1 UI overhaul (2026-05-15): structurally a fork of AgentNavBar
// so the admin SPA's top chrome matches the agent SPA's top chrome
// pixel-for-pixel (modulo the accent colour). Same sticky pill bar,
// same glass surface tokens (arena-200/60 borders, white/85 light bg,
// arena-800/70 dark bg, backdrop-blur-xl), same shadow geometry, same
// 16-row inner ratio. The only intentional differences are:
//   • Active pill uses VIOLET (admin's identity) — agent uses magenta.
//   • Permission gating mirrors the original admin TopPillNav, gated
//     via canDo() for the two agent-mode items (Players / Agents).
//   • Bell endpoint flips per role exactly as before.
//
// Centre pills hide below `lg` so the AdminBottomNavV2 (mobile bubble
// nav) can own that screen size. Brand mark + utility cluster stay
// visible at every width.

import { NavLink, useLocation } from 'react-router-dom';
import { useAdminAuth } from '../lib/adminAuth';
import AdminAvatar from './AdminAvatar';
import AdminThemeToggle from './AdminThemeToggle';
import BaaliLogo from './BaaliLogo';
import { SseAlertsBell } from '../lib/sseAlerts';

type Item = { to: string; label: string; matchPrefix?: string; onlyRole?: 'admin' | 'agent'; permissionKey?: string };

const ITEMS_ADMIN: Item[] = [
  // 2026-08-08 (UX Phase 4): Players/Agents/Bots merged into one "People"
  // destination (switched via the in-page segment bar); Game money promoted
  // to its own top-level tab. 5 slots:
  //   Dashboard · People · Game money · Offers · Settings
  { to: '/',               label: 'Dashboard' },
  { to: '/people/players', label: 'People',     matchPrefix: '/people' },
  { to: '/game-money',     label: 'Game money', matchPrefix: '/game-money' },
  // 2026-09-19: Matka King is run by hand every day (declare, hisaab,
  // play for player), so its control room is a top-level destination — the
  // owner could not find it while it lived as a card inside Game money.
  { to: '/matka',          label: 'Matka King', matchPrefix: '/matka' },
  { to: '/offers',         label: 'Offers',     matchPrefix: '/offers' },
  { to: '/settings',       label: 'Settings',   matchPrefix: '/settings' },
];

const ITEMS_AGENT: Item[] = [
  // 2026-05-16: same swap applied to the agent-role variant so the
  // top-nav surface stays consistent across roles.
  //
  // 2026-05-16 follow-up: the Money pill points directly at
  // `/people/players/requests` (the canonical destination) instead
  // of `/finance` (the legacy alias that redirects there). Eliminates
  // a wasted hop on every click. matchPrefix follows so the pill
  // still highlights when the agent lands on either /people/players
  // sub-tab — they're both "money operations" in this role's mental
  // model.
  { to: '/',                          label: 'Dashboard' },
  { to: '/people/players',            label: 'Players',  matchPrefix: '/people/players',  permissionKey: 'users' },
  { to: '/people/agents',             label: 'Agents',   matchPrefix: '/people/agents',   permissionKey: 'create_subagent' },
  { to: '/people/players/requests',   label: 'Money',    matchPrefix: '/people/players/requests' },
  { to: '/settings',                  label: 'Settings', matchPrefix: '/settings' },
];

export default function TopPillNav() {
  const { admin, canDo } = useAdminAuth();
  const loc = useLocation();
  if (!admin) return null;
  const baseItems = admin.role === 'agent' ? ITEMS_AGENT : ITEMS_ADMIN;
  const items = baseItems.filter((i) => !i.permissionKey || canDo(i.permissionKey, 'view'));
  const isActive = (i: Item) =>
    i.matchPrefix
      ? loc.pathname === i.to || loc.pathname.startsWith(i.matchPrefix + '/')
      : loc.pathname === i.to;

  return (
    <header
      // z-30 matches the player NavBar's z-index so admin/agent/player
      // headers all sit on the same plane. Padding tokens copied from
      // AgentNavBar verbatim.
      className="sticky top-0 z-30 px-3 lg:px-6 pt-3 lg:pt-5"
      style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}
    >
      <div
        // Glass strip — surface tokens, border tokens, and shadow
        // geometry copied verbatim from AgentNavBar so admin + agent
        // top bars are visually identical except for the active
        // pill's hue. `--brand-rgb` is admin's violet/fuchsia
        // (217,70,239) — set in admin-frontend/src/index.css.
        className="mx-auto max-w-screen-2xl flex items-center gap-3
                   rounded-2xl px-3 lg:px-4 h-14 lg:h-16
                   bg-white/85 dark:bg-arena-800/70 backdrop-blur-xl
                   border border-arena-200/60 dark:border-arena-600/60
                   shadow-[0_8px_24px_-12px_rgba(var(--brand-rgb),0.25)]
                   dark:shadow-[0_8px_32px_-12px_rgba(var(--brand-rgb),0.4)]"
      >
        {/* Brand block (left) — proper Baali logo + wordmark */}
        <NavLink to="/" className="shrink-0">
          <BaaliLogo role={admin.role} />
        </NavLink>

        {/* Centre pill nav (desktop only) */}
        <nav className="hidden lg:flex flex-1 items-center justify-center gap-1">
          {items.map((i) => {
            const active = isActive(i);
            return (
              <NavLink
                key={i.to}
                to={i.to}
                end={i.to === '/'}
                // Active pill uses admin's violet identity. Geometry
                // (px-4 py-1.5, rounded-full, text-[13px], font-medium)
                // matches AgentNavBar pixel-for-pixel — only the colour
                // tokens change. Inactive labels use arena/neon muted
                // tokens like AgentNavBar so the two top bars feel like
                // the same component family.
                className={`px-4 py-1.5 rounded-full text-[13px] font-medium transition
                            ${active
                              ? 'bg-violet-600 text-white shadow-[0_2px_12px_rgba(139,92,246,0.45)]'
                              : 'text-arena-700 hover:text-arena-900 dark:text-neon-200/70 dark:hover:text-neon-100'}`}
              >
                {i.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="flex-1 lg:hidden" />

        {/* Right utility cluster */}
        <div className="flex items-center gap-1.5 shrink-0">
          {/* Phase 17 FIX: removed the dead Search button — it had
              `title="Search"` but no onClick handler, so clicking did
              nothing. Will re-add when a global command palette ships. */}
          {/* Phase 13.7: notification bell. Click to open a scrollable
              panel; new alerts ring + animate the bell while panel is
              closed. Phase 19 / Batch 5 added per-row deep-linking +
              Mark-all-read; the predecessor floating banner was
              removed entirely in Batch 7. */}
          <SseAlertsBell endpoint={admin.role === 'agent'
            ? '/api/admin/me-agent/sse/alerts'
            : '/api/admin/sse/alerts'} />
          <AdminThemeToggle compact />
          <NavLink
            to="/profile"
            // Focus ring uses admin's violet accent — same purpose as
            // AgentNavBar's brand ring, just in admin's hue.
            className="ml-1 rounded-full focus:outline-none focus:ring-2 focus:ring-violet-400/50"
            title="My profile"
          >
            <AdminAvatar url={admin.avatar_url} username={admin.username} size="sm" />
          </NavLink>
        </div>
      </div>
    </header>
  );
}
